import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import { ReactNode } from 'react';
import tinycolor from 'tinycolor2';
import { Link } from 'wouter';

import useCollection from '../../hooks/useCollection';
import { useCollectionSurtitle } from '../../hooks/useSurtitle';
import { useCollectionUrlGenerator } from '../../hooks/useUrlGenerator';

import { useIsCapture } from '../../contexts/LayoutContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import Image from '../partials/Image';
import Sponsor from '../partials/Sponsor';
import Surtitle from '../partials/Surtitle';
import HeaderDescription from '../typography/HeaderDescription';
import ListTitle from '../typography/ListTitle';
import DocumentsRowBlock, { DocumentsRowBlockProps } from './DocumentsRowBlock';

import styles from '../../styles/blocks/serie-row-block.module.css';

export interface SerieRowBlockProps extends DocumentsRowBlockProps {
    slug: string;
    query?: Record<string, unknown> | null;
    listTitle?: ReactNode;
    loading?: null | 'lazy';
    className?: string | null;
    withoutBackground?: boolean;
    withLabel?: boolean;
}

const SerieRowBlock = ({
    slug,
    query = null,
    listTitle = null,
    loading = 'lazy',
    className = null,
    withoutBackground = false,
    withLabel = true,
    ...props
}: SerieRowBlockProps) => {
    const collectionUrl = useCollectionUrlGenerator();
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;
    const { collection = null } = useCollection(slug, {
        enabled: shouldLoad,
    });

    const { type, title, images, metadata, theme } = collection || {};
    const { description = null, itemsCount = 0, sponsors, brands } = metadata || {};
    const { background = null, logo = null } = images || {};
    const {
        backgroundColor = null,
        serieRowTextColor = null,
        serieRowOverlayColor = null,
    } = theme || {};
    const [sponsor = null] = sponsors || [];
    const [brand = null] = brands || [];
    const defaultBrand = useDefaultBrand();
    const { handle: brandHandle } = brand || defaultBrand || {};
    const isPodcast = type === 'serie_podcast';
    const surtitle = useCollectionSurtitle({
        ...collection,
        withCategory: true,
        withChildren: true,
        withSerieFormat: true,
    });

    const overlayColor = backgroundColor || serieRowOverlayColor || null;

    const defaultOverlayColors = {
        urbania: '#fff4e9',
        quatre95: '#EDEEFE',
        mollo: '#fff8ee',
        dehors: '#fff0e0',
        periodelibre: '#fff4e9',
        france: '#def',
    };
    const overlayColorObject =
        overlayColor !== null
            ? tinycolor(overlayColor)
            : tinycolor(defaultOverlayColors[brandHandle]);
    const overlayColorIsDark = overlayColorObject !== null && overlayColorObject.isDark();
    const overlayColorRgb = overlayColorObject !== null ? overlayColorObject.toRgb() : null;

    const finalQuery = {
        collection: collection !== null ? collection.id : slug,
        order: isPodcast && itemsCount !== null && itemsCount < 10 ? 'new' : 'old',
        ...query,
    };

    return (
        <DocumentsRowBlock
            background={
                !withoutBackground && background !== null
                    ? {
                          image: background,
                      }
                    : null
            }
            style={{
                '--card-box-translucent-background': 'rgba(255, 255, 255, 0.6)',
                '--overlay-color':
                    overlayColorRgb !== null
                        ? `${overlayColorRgb.r}, ${overlayColorRgb.g}, ${overlayColorRgb.b}`
                        : null,
                '--card-color': isPodcast
                    ? '#000'
                    : serieRowTextColor || (overlayColorIsDark ? '#fff' : '#000'),
                color: serieRowTextColor || (overlayColorIsDark ? '#fff' : '#000'),
            }}
            header={
                <div className={styles.header}>
                    <Link href={collectionUrl(collection) || '#'} className={styles.link}>
                        {logo !== null ? (
                            <Image
                                media={logo}
                                size="responsive"
                                className={styles.logo}
                                pictureClassName={styles.picture}
                            />
                        ) : null}
                    </Link>
                    {withLabel || listTitle !== null ? (
                        <Link href={collectionUrl(collection) || '#'} className={styles.label}>
                            {title !== null ? (
                                <ListTitle className={styles.title}>{title}</ListTitle>
                            ) : null}
                            {surtitle !== null ? (
                                <Surtitle surtitle={surtitle} className={styles.surtitle} />
                            ) : null}
                            {description !== null ? (
                                <HeaderDescription
                                    className={styles.description}
                                    html={description}
                                />
                            ) : null}
                            {sponsor !== null ? (
                                <Sponsor
                                    {...sponsor}
                                    withImage
                                    className={styles.sponsor}
                                    imageClassName={styles.sponsorImage}
                                />
                            ) : null}
                        </Link>
                    ) : null}
                </div>
            }
            query={finalQuery}
            disabled={collection === null}
            loading={loading}
            ref={ref}
            className={classNames([
                styles.container,
                {
                    [styles.withoutLogo]: logo === null,
                    [styles.isPodcast]: isPodcast,
                },
                className,
            ])}
            innerClassName={styles.inner}
            listClassName={styles.list}
            itemsClassName={styles.items}
            withBorderRadius={false}
            {...(isPodcast
                ? {
                      cardType: 'horizontal',
                      cardTheme: 'box-translucent',
                      cardWidth: 280,
                      cardRatio: 'square',
                      cardSize: 'medium',
                      autoColumns: true,
                      count: 8,
                      placeholderCount: 8,
                  }
                : {
                      cardType: 'vertical',
                      cardTheme: 'rounded',
                      cardWidth: 200,
                      cardRatio: 'wide',
                      cardSize: 'medium',
                      count: 6,
                      placeholderCount: 6,
                      // cardTheme: 'box',
                  })}
            {...props}
        />
    );
};

export default SerieRowBlock;
